import { Suspense } from 'react';
import { Link } from 'react-router';

import { SectionWrapper } from '@/features/marketing/components/SectionWrapper';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import { DraftNotes } from './supportDrafts';
import {
  articlePath,
  indexPath,
  indexSections,
  indexTitle,
  type SupportAudience,
  type SupportIndexSection,
} from './supportContent';

import styles from './SupportIndexPage.module.css';

interface SupportIndexPageProps {
  audience: SupportAudience;
}

const INTRO: Record<SupportAudience, string> = {
  instructor:
    'How to set up a course, build chapters, write questions and follow the class — and, first, what your students see.',
  student:
    'How to get into your course, work through a chapter and answer homework, one task at a time.',
};

function StageSection({
  headingLevel,
  section,
}: {
  headingLevel: 2 | 3;
  section: SupportIndexSection;
}) {
  const Heading = `h${headingLevel}` as const;
  const headingId = `stage-${section.stage.id}`;

  return (
    <section aria-labelledby={headingId} className={styles.stage}>
      <Heading className={styles.stageTitle} id={headingId}>
        {section.stage.title}
      </Heading>
      <ul className={styles.list}>
        {section.entries.map((entry) => (
          <li className={styles.item} key={entry.slug}>
            <Link className={styles.articleLink} to={articlePath(entry)}>
              {entry.title}
            </Link>
            {DraftNotes ? (
              <Suspense fallback={null}>
                <DraftNotes entry={entry} kind="badge" />
              </Suspense>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SupportIndexPage({ audience }: SupportIndexPageProps) {
  const title = indexTitle(audience);
  const sections = indexSections(audience);
  const other: SupportAudience = audience === 'student' ? 'instructor' : 'student';

  useDocumentTitle(title);

  const studentSections = sections.filter((section) => section.journey === 'student');
  const instructorSections = sections.filter((section) => section.journey === 'instructor');

  return (
    <SectionWrapper background="white" className={styles.section}>
      <div className={styles.page}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>Help</p>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.lead}>{INTRO[audience]}</p>
          <p className={styles.switch}>
            <Link className={styles.switchLink} to={indexPath(other)}>
              {other === 'instructor'
                ? 'Teaching with Kudu? Go to the instructor help.'
                : 'Looking for the student help?'}
            </Link>
          </p>
        </header>

        {sections.length === 0 ? (
          <p className={styles.empty}>No articles are published here yet.</p>
        ) : null}

        {audience === 'student' ? (
          studentSections.map((section) => (
            <StageSection headingLevel={2} key={section.stage.id} section={section} />
          ))
        ) : (
          <>
            {studentSections.length > 0 ? (
              <section aria-labelledby="journey-students" className={styles.journey}>
                <h2 className={styles.journeyTitle} id="journey-students">
                  What your students see
                </h2>
                {studentSections.map((section) => (
                  <StageSection headingLevel={3} key={section.stage.id} section={section} />
                ))}
              </section>
            ) : null}
            {instructorSections.length > 0 ? (
              <section aria-labelledby="journey-instructors" className={styles.journey}>
                <h2 className={styles.journeyTitle} id="journey-instructors">
                  Teaching with Kudu
                </h2>
                {instructorSections.map((section) => (
                  <StageSection headingLevel={3} key={section.stage.id} section={section} />
                ))}
              </section>
            ) : null}
          </>
        )}
      </div>
    </SectionWrapper>
  );
}
